import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Skeleton, Text } from '@kognitos/lattice';

import type { V1AutomationInputSpec } from '@/generated/hooks/grimoire/models';
import { AddTriggerPicker, ExistingTrigger } from '@/modules/triggers';

import { DraftRailSection } from './DraftRailSection';
import type { IRailTriggers } from './use-rail-triggers';

interface IRailTriggersSectionProps {
  automationId: string;
  automationName?: string;
  inputSpecs: V1AutomationInputSpec[];
  triggers: IRailTriggers;
}

/** The rail's Triggers block, with the "+" that opens the trigger-type picker. */
export function RailTriggersSection({
  automationId,
  automationName,
  inputSpecs,
  triggers,
}: IRailTriggersSectionProps) {
  const { t: tAutomation } = useTranslation('automation');
  const {
    matchedTriggers,
    hasSchedule,
    isScheduleEnabled,
    scheduleSummary,
    isTriggersLoading,
    triggerCount,
    hasWebhookTrigger,
    hasPollTrigger,
    isCapabilitiesLoading,
    canCreateSchedule,
    scheduleNeedsDefaultInputs,
  } = triggers;

  return (
    <DraftRailSection
      title={tAutomation('automation.draftRail.triggers')}
      name="triggers"
      count={triggerCount}
      defaultOpen
      /* The "+" opens the trigger-type picker directly rather than the
       * combined inputs & triggers sheet: that sheet carries its own
       * "Triggers"/"Add inputs" heading and the automation's input form,
       * both of which read as duplicates under a block already titled
       * "Triggers". */
      action={
        <AddTriggerPicker
          automationId={automationId}
          automationName={automationName}
          inputSpecs={inputSpecs}
          isDraft
          hasWebhookTrigger={hasWebhookTrigger}
          hasPollTrigger={hasPollTrigger}
          isLoadingTriggerTypes={isCapabilitiesLoading}
          hasSchedule={hasSchedule}
          canCreateSchedule={canCreateSchedule}
          scheduleNeedsDefaultInputs={scheduleNeedsDefaultInputs}
          trigger={
            <Button
              type="button"
              variant="outline"
              size="icon-xs"
              aria-label={tAutomation('automation.draftRail.addTrigger')}
              // The triggers entry point moved here from the document
              // toolbar; the draft-creation tour's "prepare to run" step
              // points at this anchor.
              data-tour-id="inputs-trigger-button"
            >
              <Icon type="Plus" size="xs" />
            </Button>
          }
        />
      }
    >
      {isTriggersLoading ? (
        <Skeleton className="h-[70px] w-full rounded-xl" />
      ) : (
        matchedTriggers.map(({ grimoireTrigger, bdkInstance }) => (
          <ExistingTrigger
            key={grimoireTrigger.trigger_id}
            isDraft
            triggerInstance={bdkInstance}
            grimoireTriggerInfo={grimoireTrigger}
            inputSpecs={inputSpecs}
            automationId={automationId}
            presentation="row"
          />
        ))
      )}
      {!isTriggersLoading && matchedTriggers.length === 0 && !hasSchedule && (
        <Text level="xSmall" color="muted" className="px-1 py-2">
          {tAutomation('automation.draftRail.noTriggers')}
        </Text>
      )}
      {hasSchedule && (
        <Flex
          align="center"
          gap={2}
          className="rounded-xl border bg-layout px-3 py-3"
        >
          <Flex
            align="center"
            justify="center"
            className="size-8 shrink-0 rounded-lg bg-muted"
          >
            <Icon type="Calendar" size="sm" />
          </Flex>
          <Flex direction="column" gap={0} className="min-w-0 flex-1">
            <Text level="xSmall" color="muted">
              {tAutomation('automation.draftRail.schedule')}
            </Text>
            <Text level="small" className="truncate">
              {scheduleSummary ||
                tAutomation('automation.draftRail.scheduleFallback')}
            </Text>
          </Flex>
          <span
            className={
              isScheduleEnabled
                ? 'size-2 rounded-full bg-success'
                : 'size-2 rounded-full bg-muted-foreground'
            }
            aria-hidden
          />
        </Flex>
      )}
    </DraftRailSection>
  );
}
